import { LeapYear, SurchargeUnit } from '../constants'
import {
  DynamicTariffSettingsDto,
  GridFeesSettingsDto,
  GridFeeState,
  ManualPowerPricesDto,
  PowerPricesSettingsDto,
  PowerPriceState,
  VariableGridFeesDto,
} from '../dto'
import { DynamicTariffSettingsModel, GridFeesSettingsModel, ManualPowerPricesModel, PowerPricesSettingsModel, VariableGridFeesModel } from '../model'
import { ALL_DAYS, dayToIndex, Days } from '@e3dc-react/shell/Libs/date/types'

/* backend expects the 'surchargePercent' between 0-1, fixed to 4 decimal points. eg: .9999 */
export const convertSurchargePercentFromModelToDto = (surchargePercent: number): string => {
  return (surchargePercent / 100).toFixed(4)
}

/* backend expects the 'vatPercent' between 0-1, fixed to 4 decimal points. eg: .9999 */
export const convertVatPercentFromModelToDto = (vatPercent: number): string => {
  return (vatPercent / 100).toFixed(4)
}

/**
 *  create date string in the format suitable for backend.
 *  insert user provided day and month into the format '1972-<month>-<day>T00:00:00.000Z'
 */
export const convertToCustomDateString = (date: Date): string => {
  const month = date.getMonth()
  const day = date.getDate()
  date.setUTCFullYear(LeapYear, month, day)
  date.setUTCHours(0, 0, 0, 0)
  return date.toISOString()
}

/**
 * create date string int the format suitable for backend.
 * insert user provided hour and minute into the format '1972-01-01T<hour>:<minute>:00.000Z'
 */
export const convertToCustomTimeString = (date: Date): string => {
  const hour = date.getHours()
  const minute = date.getMinutes()
  const normalizedDate = new Date(date)

  normalizedDate.setUTCFullYear(LeapYear, 0, 1)
  normalizedDate.setUTCHours(hour, minute, 0, 0)

  return normalizedDate.toISOString()
}

const convertWeekdaysModelToDto = (weekdays: Days): number[] => {
  return ALL_DAYS.filter(day => weekdays[day]).map(day => dayToIndex[day])
}

const convertManualPowerPricesModelToDto = (powerPrices: ManualPowerPricesModel[]): ManualPowerPricesDto[] => {
  return powerPrices.map(powerPrice => ({
    weekdays: convertWeekdaysModelToDto(powerPrice.weekdays),
    from: convertToCustomTimeString(powerPrice.from),
    to: convertToCustomTimeString(powerPrice.to),
    price: powerPrice.price,
    description: powerPrice.description,
  }))
}

const convertVariableGridFeesModelToDto = (gridFees: VariableGridFeesModel[]): VariableGridFeesDto[] => {
  return gridFees.map(gridFee => ({
    from: convertToCustomDateString(gridFee.from),
    to: convertToCustomDateString(gridFee.to),
    timeFrames: gridFee.timeFrames.map(timeFrame => ({
      from: convertToCustomTimeString(timeFrame.from),
      to: convertToCustomTimeString(timeFrame.to),
      price: timeFrame.price.toString(),
      description: timeFrame.description,
    })),
  }))
}

const convertGridFeesSettingsModelToDto = (gridFeesSettings: GridFeesSettingsModel): GridFeesSettingsDto => {
  switch (gridFeesSettings.gridFeeState) {
    case GridFeeState.Variable:
      return { gridFeeState: GridFeeState.Variable, gridFees: convertVariableGridFeesModelToDto(gridFeesSettings.gridFees) }
    case GridFeeState.Static:
      return { gridFeeState: GridFeeState.Static, gridFees: gridFeesSettings.gridFees.toString() }
    case GridFeeState.Included:
      return { gridFeeState: GridFeeState.Included, gridFees: null }
  }
}

const convertPowerPricesSettingsModelToDto = (values: PowerPricesSettingsModel): PowerPricesSettingsDto => {
  switch (values.powerPriceState) {
    case PowerPriceState.Manual:
      return {
        powerPriceState: PowerPriceState.Manual,
        powerPrices: convertManualPowerPricesModelToDto(values.powerPrices),
        vatPercent: convertVatPercentFromModelToDto(values.vatPercent),
      }
    case PowerPriceState.Static:
      return {
        powerPriceState: PowerPriceState.Static,
        powerPrices: values.powerPrices.toString(),
        vatPercent: convertVatPercentFromModelToDto(values.vatPercent),
      }
    case PowerPriceState.Dynamic: {
      /* surchargeUnit is decorative and dropped here; both fields are always sent, the inactive one as null */
      if (values.surchargeUnit === SurchargeUnit.CtPerKwh) {
        return {
          powerPriceState: PowerPriceState.Dynamic,
          taxes: values.taxes.toString(),
          vatPercent: convertVatPercentFromModelToDto(values.vatPercent),
          surchargePercent: null,
          surchargeValue: (values.surchargeValue ?? 0).toString(),
        }
      }
      return {
        powerPriceState: PowerPriceState.Dynamic,
        taxes: values.taxes.toString(),
        vatPercent: convertVatPercentFromModelToDto(values.vatPercent),
        surchargePercent: convertSurchargePercentFromModelToDto(values.surchargePercent ?? 0),
        surchargeValue: null,
      }
    }
  }
}

/**
 * Transform dynamic tariff settings model to useable format for backend
 */
export const dynamicTariffSettingsModelToDtoTransformer = (values: DynamicTariffSettingsModel): DynamicTariffSettingsDto => {
  const powerPricesSettings = convertPowerPricesSettingsModelToDto(values)
  const gridFeesSettings = convertGridFeesSettingsModelToDto(values)

  return {
    ...powerPricesSettings,
    ...gridFeesSettings,
  }
}
